import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebaseConfig';
import { ExclamationTriangleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/20/solid';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Helper function to add URL parameter
const addPaymentFailParameter = () => {
  const url = new URL(window.location);
  if (!url.searchParams.has('payment-fail-messaging')) {
    url.searchParams.set('payment-fail-messaging', '');
    window.history.replaceState({}, '', url);
  }
};

// Helper function to remove URL parameter
const removePaymentFailParameter = () => {
  const url = new URL(window.location);
  if (url.searchParams.has('payment-fail-messaging')) {
    url.searchParams.delete('payment-fail-messaging');
    window.history.replaceState({}, '', url);
  }
};

const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#ffffff',
      '::placeholder': {
        color: 'rgba(255, 255, 255, 0.6)',
      },
      backgroundColor: 'transparent',
    },
    invalid: {
      color: '#ef4444',
    },
  },
  hidePostalCode: false,
  fields: {
    number: {
      showIcon: true,
    },
  },
};

function RecoveryForm({ frozenData, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [email, setEmail] = useState('');
  const [user, setUser] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (currentUser?.email) {
        setEmail(currentUser.email);
      }
    });
    return () => unsubscribe();
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const cardElement = elements.getElement(CardElement);
      
      // Create payment method
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          email: email,
        },
      });

      if (pmError) {
        throw new Error(pmError.message);
      }

      // Call recovery API
      const response = await fetch('/api/recover-frozen-account', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          uid: user.uid,
          paymentMethodId: paymentMethod.id,
          email: email,
          frozenReason: frozenData.frozenReason,
          failedSubscriptionData: frozenData.failedSubscriptionData,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Recovery failed');
      }

      // Handle successful recovery
      setSuccess(true);
      // Remove payment failure parameter on successful recovery
      removePaymentFailParameter();
      setTimeout(() => {
        onSuccess();
      }, 2000);

    } catch (err) {
      console.error('Recovery error:', err);
      setError(err.message);
    } finally {
      setIsProcessing(false);
    }
  };



  if (success) {
    return (
      <div className="text-center">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
          <CheckCircleIcon className="h-6 w-6 text-green-600" aria-hidden="true" />
        </div>
        <h3 className="text-lg font-medium text-white mb-2">Account Recovered!</h3>
        <p className="text-white/80 mb-4">
          Your account has been successfully recovered. You're being redirected...
        </p>
        <div className="flex items-center justify-center">
          <ArrowPathIcon className="h-5 w-5 text-indigo-400 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-white/80 mb-2">
          Email Address
        </label>
        <input
          type="email"
          id="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="block w-full rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm p-3 text-white placeholder-white/40 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 focus:bg-white/10 sm:text-sm transition-all duration-200"
          placeholder="your@email.com"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-white/80 mb-2">
          New Payment Method
        </label>
        <div 
          className="block w-full rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm p-3 shadow-sm focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500 transition-all duration-200"
        >
          <CardElement options={cardElementOptions} />
        </div>
      </div>

      {error && (
        <div className="rounded-xl bg-red-500/20 backdrop-blur-sm p-4 border border-red-500/30">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2 flex-shrink-0" />
            <p className="text-sm text-red-200">{error}</p>
          </div>
        </div>
      )}

      <div>
        <button
          type="submit"
          disabled={!stripe || isProcessing}
          className="w-full rounded-xl bg-indigo-600 px-4 py-3 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
        >
          {isProcessing ? (
            <div className="flex items-center justify-center">
              <ArrowPathIcon className="h-4 w-4 animate-spin mr-2" />
              Processing...
            </div>
          ) : (
            'Subscribe'
          )}
        </button>
      </div>
    </form>
  );
}

export default function FrozenAccountRecovery({ frozenData, onSuccess }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Add payment failure parameter to URL when component mounts
  useEffect(() => {
    addPaymentFailParameter();
    
    // Clean up the parameter when component unmounts
    return () => {
      removePaymentFailParameter();
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <img
            alt="Relay"
            src="/images/logowhite.png"
            className="h-12 w-auto mx-auto mb-8"
          />
          <div className="flex items-center justify-center space-x-2">
            <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
            <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
            <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center text-white px-4"
      style={{background: 'linear-gradient(to bottom right, #0d0520, #1a0b2e, #0a0615, #050208)'}}
    >
      <div className="max-w-md w-full">
        <div 
          className="rounded-2xl border border-white/10 p-8 shadow-2xl backdrop-blur-2xl"
          style={{
            background: 'rgba(0, 0, 0, 0.3)',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.4)'
          }}
        >
          {/* Header */}
          <div className="text-center mb-8">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Payment Failed</h2>
            <p className="text-white/80 text-sm">
              You attempted to upgrade to Relay Premium + Messaging for $7.99, but your payment method was declined. Please add a new payment method to complete your upgrade.
            </p>
          </div>

          {/* Payment Details */}
          <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20">
            <h3 className="text-sm font-medium text-red-200 mb-2">Transaction Details:</h3>
            <div className="space-y-2 text-sm text-red-300">
              <div className="flex justify-between">
                <span>Plan:</span>
                <span>Relay Premium + Messaging</span>
              </div>
              <div className="flex justify-between">
                <span>Amount:</span>
                <span className="font-medium">$7.99</span>
              </div>
              <div className="flex justify-between">
                <span>Status:</span>
                <span className="text-red-400">Payment Failed</span>
              </div>
            </div>
          </div>

          {/* Recovery Form */}
          <Elements stripe={stripePromise}>
            <RecoveryForm frozenData={frozenData} onSuccess={onSuccess} />
          </Elements>
        </div>
      </div>
    </div>
  );
} 